/**
 * TypeScript mirror of `src/ui/styles/_tokens.scss`.
 *
 * Use these anywhere a colour has to be passed as a value rather than a class:
 * Chart.js datasets, `accentColor` props, Notiflix options, inline SVG fills.
 * Keep the two files in sync; never write a hex literal in a component.
 */
export const colors = {
    accent: '#5b7089',
    accentDark: '#4a5d73',
    accentSoft: '#7d8fa3',
    accentTint: '#e6ebf1',
    accentTintStrong: '#d5dde7',

    ink: '#1e293b',
    ink2: '#475569',
    ink3: '#64748b',
    ink4: '#94a3b8',
    inkInverse: '#ffffff',

    line: '#e2e8f0',
    lineStrong: '#cbd5e1',
    lineSoft: '#eef2f6',
    surface: '#ffffff',
    surface2: '#f8fafc',
    surface3: '#f3f6f9',
    surface4: '#eef2f6',

    good: '#4f7d6b',
    goodTint: '#e9f2ee',
    goodLine: '#c5dbd1',
    goodSoft: '#8fb8a6',

    bad: '#a85454',
    badTint: '#f7ecec',
    badLine: '#e6c9c9',
    badSoft: '#cf9d9d',

    warn: '#8a6420',
    warnTint: '#f8f2e6',
    warnLine: '#e6d5b3',
    warnSoft: '#cdb27a',

    info: '#4a6d8c',
    infoTint: '#e8f0f7',
    infoLine: '#c6d7e6',

    codeBg: '#0f1722',
    codeInk: '#dce6f2',
} as const

export type ColorToken = keyof typeof colors

/** rgba() helper for translucent fills built from a hex token. */
export const alpha = (hex: string, a: number): string => {
    const clean = hex.replace('#', '')
    const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean
    const n = parseInt(full, 16)
    const r = (n >> 16) & 255
    const g = (n >> 8) & 255
    const b = n & 255
    return `rgba(${r}, ${g}, ${b}, ${a})`
}

/**
 * Chart palette. `series` is an ordered list for multi-series charts (bars,
 * doughnut slices) that stays inside the slate family; semantic keys are for
 * charts where a series has a meaning (accepted vs rejected).
 */
export const chart = {
    neutral: colors.ink4,
    neutralFill: alpha(colors.ink4, 0.14),
    accent: colors.accent,
    accentFill: alpha(colors.accent, 0.12),
    good: colors.good,
    goodFill: alpha(colors.good, 0.12),
    bad: colors.bad,
    badFill: alpha(colors.bad, 0.1),
    warn: colors.warn,
    warnFill: alpha(colors.warn, 0.12),
    muted: colors.lineStrong,
    grid: colors.lineSoft,
    tick: colors.ink3,
    series: [
        '#5b7089',
        '#7d8fa3',
        '#9aa8b8',
        '#b3bdc9',
        '#8a7f6f',
        '#a89a86',
        '#6b7f7a',
        '#93a39d',
        '#c3ccd8',
        '#e2e8f0',
    ],
} as const

export const radius = { md: 10, sm: 6, xs: 4 } as const
export const font = "'Poppins', sans-serif"
export const fontMono = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

/** Sensible Chart.js defaults shared by every chart in the dashboard. */
export const chartDefaults = {
    font: { family: font, size: 11 },
    color: colors.ink3,
    gridColor: colors.lineSoft,
    borderColor: colors.surface,
    tooltip: {
        backgroundColor: colors.ink,
        titleColor: colors.inkInverse,
        bodyColor: colors.codeInk,
        cornerRadius: radius.sm,
        padding: 10,
    },
} as const
